<script lang="ts" setup>
import type { Coordinates } from '@/api';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import TurnstileModal from '@/components/TurnstileModal.vue';
import { useSubscription, type SubscriptionContextStatus } from '@/views/search/useSubscription.ts';

const props = defineProps<{
  location: Coordinates | null;
  distance: number;
  categoryIds: number[];
  contextStatus?: SubscriptionContextStatus;
}>();

const { t } = useI18n();
const contextStatus = computed(() => props.contextStatus ?? 'ready');

const { email, isLoading, isSuccess, errorMessage, isModalOpen, handleSubmit, handleTokenReceived, handleModalClose } = useSubscription(
  () => ({
    location: props.location,
    distance: props.distance,
    categoryIds: props.categoryIds,
  }),
  () => contextStatus.value
);
</script>

<template>
  <li class="outline-ph-green-300 relative rounded-lg p-0.5 outline">
    <div class="bg-ph-green-600 flex h-full w-full flex-col justify-between space-y-8 rounded-lg p-4 text-center text-white">
      <h3 class="text-xl font-semibold tracking-wide">{{ t('components.subscribe.title') }}</h3>

      <template v-if="!isSuccess">
        <form :aria-busy="isLoading || contextStatus === 'resolving'" class="space-y-2" @submit.prevent="handleSubmit">
          <div>
            <label :for="'email-input'" class="sr-only">{{ t('components.subscribe.email') }}</label>
            <input
              :id="'email-input'"
              v-model="email"
              :placeholder="t('components.subscribe.email-placeholder')"
              class="focus:outline-ph-green-400 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-2 -outline-offset-1 outline-gray-900/25 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-1 sm:text-sm/6"
              required
              type="email"
            />
          </div>

          <button
            :disabled="!email || isLoading || contextStatus !== 'ready'"
            class="text-ph-green-600 focus:outline-ph-green-400 w-full cursor-pointer rounded-md bg-white px-4 py-2 text-sm font-semibold shadow-sm outline-2 outline-offset-2 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            type="submit"
          >
            <span v-if="isLoading || contextStatus === 'resolving'">...</span>
            <span v-else>{{ t('components.subscribe.button') }}</span>
          </button>
        </form>

        <div>
          <div v-if="contextStatus === 'error'" class="rounded-md bg-white px-3 py-2 text-red-600">
            <p class="max-h-12 overflow-y-hidden text-xs/4">{{ t('components.subscribe.location-error') }}</p>
          </div>
          <p v-else-if="!errorMessage" class="text-xs tracking-tighter text-white/80">
            <span>{{ t('components.subscribe.privacy-consent') }}</span>
            <RouterLink :to="{ name: 'privacy' }" class="hover:text-ph-green-700 underline">
              {{ t('components.subscribe.privacy-policy') }}
            </RouterLink>
            <span>.</span>
          </p>
          <div v-if="errorMessage" class="rounded-md bg-white px-3 py-2 text-red-600">
            <p class="max-h-12 overflow-y-hidden text-xs/4">{{ errorMessage }}</p>
          </div>
        </div>
      </template>

      <template v-else>
        <p class="text-lg font-semibold">{{ t('components.subscribe.success') }}</p>
        <p class="text-xs tracking-tighter text-white/80">{{ t('components.subscribe.email-sent') }}</p>
      </template>
    </div>
  </li>

  <TurnstileModal :is-open="isModalOpen" @close="handleModalClose" @token-received="handleTokenReceived" />
</template>

<style lang="css" scoped></style>
